import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Accordion } from '@node-space/storybook-components'
import { Box } from '@node-space/storybook-components/dist/Box'
import { DoubleLabelSelect } from '@node-space/storybook-components/dist/DoubleLabelSelect'
import { Button, Callout, Dialog, Icon } from '@node-space/ui-components'
import Loader from 'components/spinners/LoadingAnimationDark'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { BankTransferType } from 'types/beneficiaries'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import { formatString, roundNumberWithCommas } from 'utils/utils'
import { getPaymentTitle, getWalletPropertiesForCapabilityChecks } from 'utils/wallet'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'
import SingleDepositDetails from './components/SingleDepositDetails'
import { DepositActionProps } from './Deposit'

const DepositForm = ({
  dismissAction,
  form,
  wallets,
  loading,
  accounts,
  isError,
}: DepositActionProps) => {
  const { t } = useTranslation()
  const { enableFiatCurrencyFilter } = useFeatureFlags()
  const formValues = form.watch()

  const [openAccordion, setOpenAccordion] = useState('')

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (!!wallets?.length) {
      const walletLsids = wallets?.map(wallet => wallet?.lsid)

      return getWalletActionCapabilityRequestFilters(walletLsids)
    }
  }, [wallets])

  const { data: bulkCapabilities, isFetching: isFetchingCapabilities } = useBulkCapabiltiesQuery(
    fiatWalletCapabilitiesRequest,
    !!fiatWalletCapabilitiesRequest
  )

  const supportedWallets = useMemo(() => {
    return wallets.filter(wallet => {
      const walletProperties = getWalletPropertiesForCapabilityChecks(wallet)
      const { supportsDeposits } = getFiatWalletActionSupport({
        walletProperties,
        bulkCapabilities,
        enableFiatCurrencyFilter,
      })

      return wallet?.currency?.code && wallet?.currency?.fiat && supportsDeposits
    })
  }, [wallets, bulkCapabilities])

  const selectedWallet =
    formValues?.walletId && supportedWallets.find(x => x?.id.toString() === formValues?.walletId)

  const accordionItems = useMemo(() => {
    const localPaymentMethods = []
    const internationalPaymentMethods = []

    accounts?.forEach(account => {
      const accordionItem = {
        name: account?.accountNumber,
        title: getPaymentTitle(account, t),
        children: <SingleDepositDetails account={account} wallet={selectedWallet} />,
      }
      account?.isSwift || account?.accountNumberFormat === BankTransferType.SWIFT
        ? internationalPaymentMethods.push(accordionItem)
        : localPaymentMethods.push(accordionItem)
    })
    return [...localPaymentMethods, ...internationalPaymentMethods]
  }, [accounts])

  const isLoading = loading || isFetchingCapabilities
  const isGenericError = !accordionItems?.length && !isLoading && isError

  return (
    <Dialog
      title={t('deposit')}
      open
      onClose={dismissAction}
      ActionButtons={[
        <Button key="done" onClick={dismissAction} loading={isLoading || !selectedWallet}>
          {t('done')}
        </Button>,
      ]}
    >
      <Box testid="depositForm">
        <DoubleLabelSelect
          loading={!selectedWallet || isFetchingCapabilities}
          iconsUrl={process.env.ICONS_URL}
          name={t('selectWallet')}
          label={t('selectWallet')}
          showOverflow
          menuMaxHeight={250}
          placeholder={
            !wallets
              ? t('loadingEllipses')
              : wallets && wallets?.length === 0
                ? t('noWallets')
                : t('searchOrSelectAWallet')
          }
          options={supportedWallets?.map(wallet => ({
            icon: wallet?.currency?.code?.toLowerCase(),
            value: wallet?.id?.toString(),
            label: wallet?.description,
            secondLabel: formatString(
              t('balanceWithCurrency'),
              roundNumberWithCommas(wallet?.balance),
              wallet?.currency?.code
            ),
          }))}
          value={selectedWallet?.id?.toString()}
          isSearchable={true}
          iconLeft={!selectedWallet && <Icon name="MagnifyingGlass" />}
          onChange={(value: string) => form.reset({ walletId: value }, { keepDefaultValues: true })}
        />

        {(isLoading || !selectedWallet) && !isGenericError && (
          <Box flex justifyContent="center" paddingT={32}>
            {Loader}
          </Box>
        )}

        {!isLoading && !!selectedWallet && (isGenericError || !accordionItems?.length) ? (
          <Box flex width={'full'} paddingT={24}>
            <Callout variant="error">{t('oopsSomethingWentWrongFetchDespoitDetails')}</Callout>
          </Box>
        ) : (
          <>
            {!isLoading && !!accordionItems?.length && !!selectedWallet && (
              <Box paddingT={20}>
                {accordionItems?.length > 1 ? (
                  <Accordion
                    isSlim={true}
                    items={accordionItems}
                    bodyClassOverrides="w-full pr-[15px]"
                    borderless={true}
                    getOpenName={name => {
                      setOpenAccordion(name)
                    }}
                    defaultOpen={openAccordion}
                  />
                ) : (
                  <Box paddingX={20}>
                    <SingleDepositDetails account={accounts?.[0]} wallet={selectedWallet} />
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  )
}

export default DepositForm
